/* ═══════════════════════════════════════════════════════════════════════════
   The supply side — companies, the people inside them, and how they get here
   ───────────────────────────────────────────────────────────────────────────
   Everything above this module assumes a partner exists to send. This is
   where one comes from, and it answers three questions the product could not
   previously answer at all:

     Who is this?      An application, its documents, and a decision somebody
                       signed. Not a form that emails an inbox.
     Who are they?     `owner`, `dispatcher` and `worker` inside one account.
                       A laundry with eight staff and three drivers cannot be
                       one phone, and a freelancer must never pay for the
                       distinction — she holds all three and is shown none.
     Are they live?    Approval creates a `Provider`. That mapping is the join
                       between the two halves of the product, and until it
                       existed an approved partner was a row in a table that
                       no customer could ever be sent to.

   The module is pure: tables, predicates and one conversion. It reads no
   storage, imports no seed data and calls no clock it was not handed, because
   every rule in here is one the Go service will have to enforce too, and a
   rule that can only be checked inside a React app is a rule the server does
   not have. lib/api.ts owns persistence and ids; this owns what is legal.

   Why the roles are capabilities rather than a rank: they are not ordered. A
   dispatcher outranks a worker on assignment and holds nothing over them on
   payout details, which belong to the owner alone. `dispatcher > worker` as a
   number is a bug the first time somebody asks whether a dispatcher can see
   the bank account.
   ═══════════════════════════════════════════════════════════════════════════ */

import {
  HexApiError,
  type ApplicationStatus,
  type CategoryId,
  type DocumentKind,
  type LocalizedText,
  type PartnerAccount,
  type PartnerApplication,
  type PartnerDocument,
  type PartnerKind,
  type Provider,
  type ProviderDraft,
  type ServiceCategory,
  type StaffMember,
  type StaffRole,
} from './types'

/* ── roles ────────────────────────────────────────────────────────────────── */

/** Every distinct thing a person inside a partner account can do.
 *
 *  Written as verbs against one screen each, because a capability that maps to
 *  no button is a permission nobody can reason about — and one button gated on
 *  two capabilities is a screen that will eventually be half-visible. */
export type Capability =
  /** Take the job the platform offered, against the 90-second clock. */
  | 'accept-offer'
  /** Put a named person on an accepted job. */
  | 'assign-job'
  /** Drive the customer's timeline: on my way, arrived, started, done. */
  | 'run-job'
  /** Hours, days off, and how many jobs to take in a window — the number the
   *  customer's slot picker consumes. */
  | 'set-availability'
  /** The customer's address and masked number. Scoped to the job in hand for
   *  a worker; a dispatcher sees the ones they are dispatching. */
  | 'view-customer-contact'
  /** The account's money: gross, commission, net, next payout. */
  | 'view-earnings'
  /** Add, suspend and re-role the people in the account. */
  | 'manage-staff'
  /** IBAN, CR, VAT — the details money and liability hang off. */
  | 'manage-payout-details'

/** The whole grant, per role.
 *
 *  A worker deliberately cannot `view-earnings`: that is the account's revenue,
 *  and an employee opening the app to start a job should not be reading what
 *  the firm bills. Their own pay is a payslip, which is a different screen and
 *  a different question.
 *
 *  A dispatcher deliberately cannot `run-job`. In a small firm the dispatcher
 *  also drives — and then they hold both roles, which the model already allows.
 *  Folding `run-job` into `dispatcher` would instead make it impossible to
 *  express the eight-person laundry where the person on the phone never leaves
 *  the counter. */
export const ROLE_CAPABILITIES: Record<StaffRole, readonly Capability[]> = {
  owner: [
    'accept-offer',
    'assign-job',
    'run-job',
    'set-availability',
    'view-customer-contact',
    'view-earnings',
    'manage-staff',
    'manage-payout-details',
  ],
  dispatcher: ['accept-offer', 'assign-job', 'set-availability', 'view-customer-contact'],
  worker: ['run-job', 'view-customer-contact'],
}

/** One person wearing all three hats. A `freelancer` and a `mobile` operator
 *  are this by definition: there is nobody else in the account to be anything
 *  else, and asking them to pick a role would be asking them to answer a
 *  question about an org chart of one. */
export const SOLO_ROLES: readonly StaffRole[] = ['owner', 'dispatcher', 'worker']

export function isSolo(kind: PartnerKind): boolean {
  return kind !== 'company'
}

/** The roles the first staff member gets when approval creates the account.
 *  A company's owner is an owner; everyone else they hire, they hire. */
export function foundingRoles(kind: PartnerKind): StaffRole[] {
  return isSolo(kind) ? [...SOLO_ROLES] : ['owner']
}

export function capabilitiesOf(roles: readonly StaffRole[]): Capability[] {
  const all = new Set<Capability>()
  for (const role of roles) for (const c of ROLE_CAPABILITIES[role]) all.add(c)
  return [...all]
}

/** The one permission check. Everything that gates a control asks this, so
 *  there is exactly one place to be wrong and exactly one place to fix it. */
export function can(roles: readonly StaffRole[], capability: Capability): boolean {
  return roles.some((role) => ROLE_CAPABILITIES[role].includes(capability))
}

/** Whether a staff member can be sent on a job right now.
 *
 *  Three things have to hold and they fail for different reasons: the role
 *  (a dispatcher is not a spare pair of hands), the status (an invited driver
 *  has not signed in yet, a suspended one is stood down for cause), and the
 *  district — a worker scoped to two districts is scoped to them because their
 *  own transport does not reach further. */
export function canWork(staff: StaffMember, area?: string | null): boolean {
  if (staff.status !== 'active') return false
  if (!can(staff.roles, 'run-job')) return false
  if (!area || staff.areas.length === 0) return true
  return staff.areas.includes(area)
}

/** Who a dispatcher can actually put on this job. Sorted by name so the list
 *  does not reorder itself between two openings of the same sheet. */
export function assignableStaff(
  staff: readonly StaffMember[],
  area?: string | null,
): StaffMember[] {
  return staff.filter((s) => canWork(s, area)).toSorted((a, b) => a.name.localeCompare(b.name))
}

/** An account with no owner is an account nobody can be paid into, and the
 *  most likely way to create one is suspending the last one by accident. */
export function hasActiveOwner(staff: readonly StaffMember[]): boolean {
  return staff.some((s) => s.status === 'active' && s.roles.includes('owner'))
}

/* ── documents ────────────────────────────────────────────────────────────── */

/** What each shape of partner has to produce before anybody is sent anywhere.
 *
 *  A freelancer is two files and a company is four, and that asymmetry is the
 *  point: the paperwork tracks the risk. A man with a van can flood a flat, so
 *  he is insured; a company sends people whose names we never took, so its
 *  workers are documented one by one below. */
export const DOCUMENTS_REQUIRED: Record<PartnerKind, readonly DocumentKind[]> = {
  freelancer: ['national-id', 'bank-letter'],
  mobile: ['national-id', 'bank-letter', 'insurance', 'vehicle'],
  company: ['national-id', 'cr', 'bank-letter', 'insurance'],
}

/** Every person a company sends to a door, not merely the owner who signed up.
 *  The whole promise of a vetted marketplace is that the stranger at the door
 *  was checked, and "we checked the man who owns the company" is not that. */
export const STAFF_DOCUMENTS_REQUIRED: readonly DocumentKind[] = ['national-id']

/** An identity document belongs to a person; a CR belongs to a business. The
 *  distinction is invisible on an application — there is one applicant and no
 *  staff records yet — and becomes load-bearing the moment a company hires its
 *  second driver, because the business's file is complete and that driver is
 *  still a stranger.
 *
 *  So the same list is read two ways: everything, against the applicant, to
 *  decide whether to approve; and split, against the account and each person
 *  in it, to decide whether anybody may be sent anywhere. */
export const PERSONAL_DOCUMENTS: readonly DocumentKind[] = ['national-id']

/** Documents that go stale. A bank letter states a fact that does not expire;
 *  an Iqama, a CR, a VAT certificate, an insurance policy and a vehicle
 *  registration all do, and each of them lapsing is a reason a partner cannot
 *  legally work tomorrow. */
export const EXPIRING_DOCUMENTS: readonly DocumentKind[] = [
  'national-id',
  'cr',
  'vat',
  'insurance',
  'vehicle',
]

/** How long before expiry we ask for the replacement. Thirty days is roughly
 *  what a Saudi insurance renewal takes end to end, and asking on the morning
 *  it lapses means standing a partner down mid-week over paperwork that was
 *  always going to arrive. */
export const RENEWAL_WINDOW_DAYS = 30

/** The list for one applicant. VAT is conditional because registration is:
 *  below the threshold a company genuinely has no certificate to send, and
 *  demanding one is how a real business decides the form is broken. */
export function documentsRequired(
  kind: PartnerKind,
  opts: { vatRegistered?: boolean } = {},
): DocumentKind[] {
  const base = [...DOCUMENTS_REQUIRED[kind]]
  if (kind === 'company' && opts.vatRegistered) base.push('vat')
  return base
}

/** Required documents that are not yet verified — which is the operator's
 *  actual question, and is not the same as "not yet uploaded": a rejected
 *  scan and a missing one both mean this partner cannot go live. */
export function missingDocuments(
  kind: PartnerKind,
  docs: readonly PartnerDocument[],
  opts: { vatRegistered?: boolean } = {},
): DocumentKind[] {
  const verified = new Set(
    docs.filter((d) => d.status === 'verified' && !d.staffId).map((d) => d.kind),
  )
  return documentsRequired(kind, opts).filter((k) => !verified.has(k))
}

/** The business's own file, once the account exists: everything that is not
 *  somebody's identity. The admin panel's go-live checklist is this list and
 *  the per-person one below it. */
export function missingBusinessDocuments(
  account: PartnerAccount,
  docs: readonly PartnerDocument[],
): DocumentKind[] {
  const verified = new Set(
    docs.filter((d) => d.status === 'verified' && !d.staffId).map((d) => d.kind),
  )
  return documentsRequired(account.kind, { vatRegistered: Boolean(account.vatNumber) })
    .filter((k) => !PERSONAL_DOCUMENTS.includes(k))
    .filter((k) => !verified.has(k))
}

/** Whether this particular person may be sent to a door. */
export function isDocumented(
  staffId: string,
  docs: readonly PartnerDocument[],
): boolean {
  const held = new Set(
    docs.filter((d) => d.staffId === staffId && d.status === 'verified').map((d) => d.kind),
  )
  return STAFF_DOCUMENTS_REQUIRED.every((k) => held.has(k))
}

export type DocumentHealth = 'ok' | 'expiring' | 'expired'

/** Where a verified document stands against the calendar. Documents that do
 *  not expire, and documents with no expiry recorded, are `ok` — an absent
 *  date is not a lapsed one, and treating it as one would stand down every
 *  partner onboarded before the field existed. */
export function documentHealth(doc: PartnerDocument, now: Date = new Date()): DocumentHealth {
  if (doc.status === 'expired') return 'expired'
  if (!doc.expiresAt) return 'ok'
  const expires = new Date(doc.expiresAt).getTime()
  if (Number.isNaN(expires)) return 'ok'
  if (expires <= now.getTime()) return 'expired'
  return expires <= now.getTime() + RENEWAL_WINDOW_DAYS * 86_400_000 ? 'expiring' : 'ok'
}

/** The renewal queue: what to ask for before it lapses, soonest first. This
 *  list being empty is the only version of compliance anybody can operate. */
export function documentsNeedingRenewal(
  docs: readonly PartnerDocument[],
  now: Date = new Date(),
): PartnerDocument[] {
  return docs
    .filter(
      (d) =>
        (d.status === 'verified' || d.status === 'expired') && documentHealth(d, now) !== 'ok',
    )
    .toSorted((a, b) => (a.expiresAt ?? '').localeCompare(b.expiresAt ?? ''))
}

/** Whether an application can legally be approved. Deliberately a hard gate
 *  rather than a warning: the one thing this pipeline exists to prevent is an
 *  undocumented person standing in a customer's hallway, and a soft check is
 *  one an operator clears at 4pm on a Thursday. */
export function isApprovable(
  app: PartnerApplication,
  docs: readonly PartnerDocument[],
): boolean {
  if (app.status !== 'reviewing') return false
  return missingDocuments(app.kind, docs, { vatRegistered: Boolean(app.vatRegistered) }).length === 0
}

/* ── the application lifecycle ────────────────────────────────────────────── */

/** Which moves are legal, from every state.
 *
 *  `docs-requested` returns to `reviewing` and never straight to `approved`:
 *  the file has to be looked at by somebody after it arrives, and a state
 *  machine that lets the upload itself approve the application is one where
 *  the applicant approves themselves.
 *
 *  The three terminal states have no exits at all. A rejected applicant
 *  re-applies — a new application, with its own reference and its own
 *  decision — rather than having an old row quietly reopened underneath a
 *  decision somebody already signed. */
export const APPLICATION_FLOW: Record<ApplicationStatus, readonly ApplicationStatus[]> = {
  received: ['reviewing', 'rejected', 'withdrawn'],
  reviewing: ['docs-requested', 'approved', 'rejected', 'withdrawn'],
  'docs-requested': ['reviewing', 'withdrawn', 'rejected'],
  approved: [],
  rejected: [],
  withdrawn: [],
}

export function nextStatuses(from: ApplicationStatus): readonly ApplicationStatus[] {
  return APPLICATION_FLOW[from]
}

export function canTransition(from: ApplicationStatus, to: ApplicationStatus): boolean {
  return APPLICATION_FLOW[from].includes(to)
}

/** The operator's board, which is one pipeline even though the data behind it
 *  is two records. An application ends at `approved`; a partner then onboards
 *  and goes live. Splitting them in the model is right — a suspension in 2029
 *  has no business editing a 2026 application — and showing them split would
 *  hide the only number supply cares about: how many of the people who applied
 *  this month are taking jobs. */
export const PIPELINE = [
  'received',
  'reviewing',
  'docs-requested',
  'approved',
  'onboarding',
  'active',
] as const

export type PipelineStage = (typeof PIPELINE)[number] | 'rejected' | 'withdrawn'

/** Where one applicant sits on that board.
 *
 *  A `paused` or `suspended` partner still reads `active` here, because this
 *  answers "did this application make it to a working partner" and the answer
 *  is yes. What they are doing today is `PartnerAccount.status`, and it is
 *  shown next to this rather than folded into it. */
export function pipelineStage(
  app: PartnerApplication,
  account?: PartnerAccount | null,
): PipelineStage {
  if (app.status !== 'approved') return app.status
  if (!account) return 'approved'
  return account.status === 'onboarding' ? 'onboarding' : 'active'
}

/* ── approval: an application becomes a listing ───────────────────────────────
   The join between the two halves of the product. An application carries a
   name, categories, districts and a kind. A listing needs a slug, initials, a
   series slot, hours, a minimum order and a price list. The operator supplies
   the difference — and the record is *derived* from the application rather
   than typed fresh, because an approved partner who then has to be re-entered
   by hand is exactly how one ends up live in the wrong district. */

/** Latin slug from a trading name. Arabic-only names produce nothing here on
 *  purpose: a percent-encoded Arabic slug is unreadable in a URL bar, unusable
 *  in a support call and different in every browser's address bar. The caller
 *  falls back to the category and the application reference, which is at least
 *  something a human can read down a phone. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Slugs are in a customer-visible URL, so a collision is not an internal
 *  detail — `/p/al-noor` resolving to whichever of two car washes was seeded
 *  first is a customer driving to the wrong forecourt. */
export function uniqueSlug(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base}-${n}`
    if (!taken.has(candidate)) return candidate
  }
  throw new HexApiError('conflict', 'Could not derive a unique slug', 'slug')
}

/** Two letters on a series-hue tile, which is what sits on the card until a
 *  cover photo exists. Falls back to the slug rather than to the Arabic name:
 *  two Arabic letters in a 32px Latin-cased avatar render as a box. */
export function initialsOf(name: LocalizedText, slug: string): string {
  const words = name.en.replace(/[^A-Za-z ]/g, ' ').split(' ').filter(Boolean)
  const picked = words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')
  return picked || slug.replace(/[^a-z0-9]/g, '').slice(0, 2).toUpperCase() || 'HX'
}

/** What the conversion needs from outside itself. Passed in rather than
 *  imported so this module stays free of seed data and of storage: the same
 *  function runs in a test with two fixtures and in the service with a table. */
export interface ApprovalContext {
  /** The category as the catalogue defines it — the source of the defaults an
   *  operator did not override. */
  category: ServiceCategory
  /** Every slug already in use, seeded and approved alike. */
  taken: ReadonlySet<string>
  /** The districts this market serves, from `GET /areas`.
   *
   *  Required, and not defaulted to a compiled table. A partner who named no
   *  district said "wherever you send me", which is a legitimate answer and
   *  must not become "nowhere" — so it becomes *everywhere we serve*, and what
   *  we serve is the server's answer. Falling back to a bundled list here
   *  wrote districts into a real provider record that the API may not have,
   *  which is coverage nobody can be dispatched to. */
  allAreas: readonly string[]
}

/**
 * Build the listing an approved application becomes.
 *
 * Pure and total: it either returns a `Provider` that is safe to publish or it
 * throws a typed validation error naming the field. Nothing is defaulted
 * silently that would change who the customer is sent to — districts and
 * venues are the two that decide that, and both are checked rather than
 * guessed.
 */
export function providerFromApplication(
  app: PartnerApplication,
  draft: ProviderDraft,
  ctx: ApprovalContext,
): Provider {
  if (!app.categoryIds.includes(draft.categoryId)) {
    throw new HexApiError(
      'validation',
      'They did not apply for that category',
      'categoryId',
    )
  }
  if (draft.categoryId !== ctx.category.id) {
    throw new HexApiError('validation', 'Category mismatch', 'categoryId')
  }

  const venues = draft.venues ?? ['we-come']
  if (venues.length === 0) {
    throw new HexApiError('validation', 'A listing needs at least one venue', 'venues')
  }
  for (const venue of venues) {
    if (!ctx.category.venues.includes(venue)) {
      throw new HexApiError(
        'validation',
        'That category is not fulfilled that way',
        'venues',
      )
    }
  }

  /* Premises are the whole reason `you-come` exists, and only a registered
     business can hold them. Publishing a forecourt listing without an address
     sends a customer driving to a marker that is not there — the one failure
     in this product with a petrol cost attached. */
  const driveIn = venues.includes('you-come')
  if (driveIn && app.kind !== 'company') {
    throw new HexApiError(
      'validation',
      'Only a company can hold premises a customer drives to',
      'venues',
    )
  }
  if (driveIn && !draft.site) {
    throw new HexApiError('validation', 'A drive-in listing needs its site', 'site')
  }
  if (!driveIn && draft.site) {
    throw new HexApiError(
      'validation',
      'A listing with a site has to offer it',
      'venues',
    )
  }

  const name: LocalizedText = draft.name ?? {
    en: app.businessName ?? app.fullName,
    ar: app.businessName ?? app.fullName,
  }

  /* Ref rather than a counter: it is already unique, already unambiguous read
     aloud, and it means a support call about `/p/carwash-K7P-42Q` can be
     traced back to the application without a lookup. */
  const base = slugify(name.en) || `${draft.categoryId}-${app.ref.toLowerCase()}`
  const slug = uniqueSlug(draft.slug ? slugify(draft.slug) : base, ctx.taken)

  const areas = draft.areas ?? (app.areas.length > 0 ? app.areas : [...ctx.allAreas])
  if (areas.length === 0 && !driveIn) {
    throw new HexApiError(
      'validation',
      'A partner who travels has to travel somewhere',
      'areas',
    )
  }

  const priceLevel = draft.priceLevel ?? 2

  return {
    id: `p_${slug}`,
    slug,
    name,
    categoryId: draft.categoryId,
    kind: app.kind,
    venues,
    ...(draft.site ? { site: draft.site } : {}),
    initials: initialsOf(name, slug),
    slot: draft.slot ?? ctx.category.slot,
    /* Nobody arrives with a rating. Zero reviews and a zero score is the
       honest state, and it is why `matchPartner` weights by review volume:
       a new partner has to be given work by a rule that knows they are new,
       not flattered with a 5.0 nobody earned. */
    rating: 0,
    reviewCount: 0,
    etaMinutes: draft.etaMinutes ?? (ctx.category.flow === 'appointment' ? null : [45, 90]),
    minOrder: draft.minOrder ?? ctx.category.minOrder,
    deliveryFee: draft.deliveryFee ?? ctx.category.deliveryFee,
    priceLevel,
    /* `new` is the only badge approval can hand out. The others are earned
       from data this partner does not have yet, and a `top-rated` badge on a
       partner with no reviews is the fastest way to make every badge on the
       platform worthless. */
    badges: ['new'],
    areas,
    about: draft.about ?? name,
    openFrom: draft.openFrom ?? ctx.category.openFrom,
    openTo: draft.openTo ?? ctx.category.openTo,
    promo: null,
  }
}

/** The account approval creates, minus the ids and timestamps the service
 *  assigns. One account however many categories they were approved for: a
 *  handyman who also services air conditioning is one contract, one IBAN and
 *  one person to call, listed twice. */
export function accountFromApplication(
  app: PartnerApplication,
  categoryIds: CategoryId[],
): Omit<PartnerAccount, 'id' | 'applicationId' | 'providerIds' | 'createdAt'> {
  return {
    kind: app.kind,
    name: { en: app.businessName ?? app.fullName, ar: app.businessName ?? app.fullName },
    phone: app.phone,
    ...(app.email ? { email: app.email } : {}),
    status: 'onboarding',
    categoryIds,
    areas: app.areas,
    commissionBps: null,
    ...(app.crNumber ? { crNumber: app.crNumber } : {}),
  }
}

/** A partner is listable when their own house is in order. Checked at the
 *  moment of going live rather than only at approval, because the gap between
 *  the two is where the driver whose Iqama was never uploaded gets forgotten. */
export function readyToActivate(
  account: PartnerAccount,
  staff: readonly StaffMember[],
  docs: readonly PartnerDocument[],
  now: Date = new Date(),
): { ready: boolean; blockers: string[] } {
  const blockers: string[] = []

  if (account.providerIds.length === 0) blockers.push('no-listing')
  if (!hasActiveOwner(staff)) blockers.push('no-owner')
  if (!account.ibanLast4) blockers.push('no-bank-details')

  const missing = missingBusinessDocuments(account, docs)
  if (missing.length > 0) blockers.push(...missing.map((k) => `missing-${k}`))

  /* A document verified last year whose date has since passed. Nothing flips
     it to `expired` on its own — the calendar does that, and only a check
     against the calendar notices. */
  if (docs.some((d) => d.status === 'verified' && documentHealth(d, now) === 'expired')) {
    blockers.push('expired-document')
  }

  /* Everyone who will be at a door needs their own identity on file, and a
     company that has not staffed itself cannot send anybody at all. A
     freelancer clears both lines with one document, because she is the whole
     company and her ID came in with the application. */
  const workers = staff.filter((s) => s.status !== 'suspended' && s.roles.includes('worker'))
  if (workers.length === 0) blockers.push('no-workers')
  for (const worker of workers) {
    if (!isDocumented(worker.id, docs)) blockers.push(`undocumented-${worker.id}`)
  }

  return { ready: blockers.length === 0, blockers }
}
